import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, BookOpen, Scale, Anchor, Car, Shield, AlertTriangle, ChevronRight, Search } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const LegalGlossaryPage = () => {
  const [searchTerm, setSearchTerm] = React.useState('');

  // Glossary terms organized by category
  const glossaryTerms = [
    // Maritime Law Terms
    {
      term: "Jones Act",
      category: "Maritime Law",
      definition: "A federal law (46 U.S.C. § 30104) that allows seamen injured in the course of their employment to sue their employers for negligence. Unlike workers' compensation, the Jones Act provides the right to a jury trial and recovery for pain and suffering.",
      relatedLink: "/maritime-injuries",
      relatedText: "Learn about maritime injury claims"
    },
    {
      term: "Maintenance and Cure",
      category: "Maritime Law",
      definition: "A maritime doctrine requiring employers to pay injured seamen's living expenses (maintenance) and medical expenses (cure) until they reach maximum medical improvement, regardless of fault.",
      relatedLink: "/blog/maintenance-and-cure-explained",
      relatedText: "Read full article on maintenance and cure"
    },
    {
      term: "Seaman",
      category: "Maritime Law",
      definition: "Under maritime law, a worker who spends approximately 30% or more of their working time aboard a vessel in navigation and contributes to the vessel's function or mission. Seamen are entitled to special protections under the Jones Act.",
      relatedLink: "/maritime-injuries",
      relatedText: "Maritime injury information"
    },
    {
      term: "Unseaworthiness",
      category: "Maritime Law",
      definition: "A maritime legal doctrine holding vessel owners strictly liable when a vessel or its equipment is not reasonably fit for its intended purpose, causing injury to a seaman. No proof of negligence is required.",
      relatedLink: "/maritime-injuries",
      relatedText: "Learn about unseaworthiness claims"
    },
    {
      term: "Longshore and Harbor Workers' Compensation Act (LHWCA)",
      category: "Maritime Law",
      definition: "A federal law providing compensation benefits to maritime workers (other than seamen) who are injured on navigable waters or adjoining areas like docks and terminals.",
      relatedLink: "/maritime-injuries",
      relatedText: "Maritime worker protections"
    },
    // Personal Injury Terms
    {
      term: "Statute of Limitations",
      category: "Personal Injury",
      definition: "The legal deadline by which a lawsuit must be filed. In Washington State, most personal injury claims must be filed within 3 years of the injury date (RCW 4.16.080). Missing this deadline typically bars recovery.",
      relatedLink: "/blog/washington-personal-injury-statute-limitations",
      relatedText: "Washington statute of limitations guide"
    },
    {
      term: "Contingency Fee",
      category: "Personal Injury",
      definition: "A payment arrangement where the attorney's fee is a percentage of the recovery, paid only if the case is successful. This means no upfront costs to the client and the attorney shares the risk.",
      relatedLink: "/pricing",
      relatedText: "View pricing information"
    },
    {
      term: "Negligence",
      category: "Personal Injury",
      definition: "The failure to exercise reasonable care that a prudent person would use under similar circumstances, resulting in harm to another. Proving negligence requires showing duty, breach, causation, and damages.",
      relatedLink: "/faqs",
      relatedText: "FAQs about injury cases"
    },
    {
      term: "Comparative Negligence",
      category: "Personal Injury",
      definition: "Washington follows 'pure comparative negligence' (RCW 4.22), meaning your compensation is reduced by your percentage of fault. Even if you're 99% at fault, you can recover 1% of damages.",
      relatedLink: "/faqs",
      relatedText: "Learn more in our FAQs"
    },
    {
      term: "Res Ipsa Loquitur",
      category: "Personal Injury",
      definition: "Latin for 'the thing speaks for itself.' A legal doctrine that allows negligence to be inferred when an accident is of a type that ordinarily would not occur without negligence — for example, a surgical instrument left inside a patient. Washington courts apply this doctrine in appropriate medical malpractice and premises liability cases.",
      relatedLink: "/case-results/retained-surgical-tool-settlement",
      relatedText: "See how this applied in a real case"
    },
    {
      term: "Product Liability",
      category: "Personal Injury",
      definition: "The legal responsibility of manufacturers, distributors, and sellers for injuries caused by defective products. A defective product may share responsibility for an injury alongside the person or business that used it — including when a defective surgical instrument breaks during a procedure.",
      relatedLink: "/case-results",
      relatedText: "Case results involving product defects"
    },
    {
      term: "Retained Surgical Object",
      category: "Personal Injury",
      definition: "Any instrument, sponge, needle, or fragment of an instrument left inside a patient after surgery. Even when a surgical count is reported as correct, a broken piece of an instrument that remains inside can still support a negligence claim.",
      relatedLink: "/med-malpractice",
      relatedText: "Learn about medical malpractice cases"
    },
    {
      term: "Constructive Notice",
      category: "Premises Liability",
      definition: "A legal concept that a business is treated as knowing about a hazard if the hazard existed long enough that the business, exercising reasonable care, should have discovered it. Constructive notice can support a premises liability claim even when the business did not have actual knowledge of the dangerous condition.",
      relatedLink: "/slip-and-falls",
      relatedText: "Learn about slip and fall cases"
    },
    {
      term: "Damages",
      category: "Personal Injury",
      definition: "Monetary compensation awarded to an injured party. Includes economic damages (medical bills, lost wages) and non-economic damages (pain and suffering, emotional distress).",
      relatedLink: "/compensation-estimator",
      relatedText: "Estimate your compensation"
    },
    {
      term: "Pain and Suffering",
      category: "Personal Injury",
      definition: "Non-economic damages compensating for physical pain, emotional distress, loss of enjoyment of life, and mental anguish caused by an injury. These are not covered by workers' compensation.",
      relatedLink: "/compensation-estimator",
      relatedText: "Calculate potential compensation"
    },
    {
      term: "Maximum Medical Improvement (MMI)",
      category: "Personal Injury",
      definition: "The point at which an injured person's condition has stabilized and is unlikely to improve further with additional treatment. Important for calculating future medical needs and settling claims.",
      relatedLink: "/faqs",
      relatedText: "Frequently asked questions"
    },
    // First Responder Terms
    {
      term: "Third-Party Claim",
      category: "First Responder",
      definition: "A civil lawsuit against someone other than your employer (such as a negligent driver or criminal) for injuries sustained on the job. First responders can pursue these claims in addition to workers' compensation.",
      relatedLink: "/first-responder-crashes",
      relatedText: "First responder injury rights"
    },
    {
      term: "Workers' Compensation",
      category: "First Responder",
      definition: "A no-fault insurance system providing medical care and wage replacement for workers injured on the job. Benefits are limited and don't include pain and suffering, which is why third-party claims are important.",
      relatedLink: "/first-responder-crashes",
      relatedText: "Beyond workers' comp"
    },
    // Premises Liability Terms
    {
      term: "Premises Liability",
      category: "Premises Liability",
      definition: "The legal responsibility of property owners and occupiers to maintain safe conditions for visitors. If a hazardous condition causes injury, the owner may be liable for negligence.",
      relatedLink: "/slip-and-falls",
      relatedText: "Slip and fall claims"
    },
    {
      term: "Duty of Care",
      category: "Premises Liability",
      definition: "The legal obligation to act with reasonable care to prevent foreseeable harm. Property owners owe varying duties depending on whether visitors are invitees, licensees, or trespassers.",
      relatedLink: "/slip-and-falls",
      relatedText: "Premises liability information"
    },
    // Wrongful Death Terms
    {
      term: "Wrongful Death",
      category: "Wrongful Death",
      definition: "A civil claim brought when someone dies due to another's negligence or intentional act. In Washington (RCW 4.20.010), the personal representative of the estate files on behalf of beneficiaries.",
      relatedLink: "/wrongful-death",
      relatedText: "Wrongful death claims"
    },
    {
      term: "Personal Representative",
      category: "Wrongful Death",
      definition: "The person appointed to manage a deceased person's estate and file legal claims on behalf of beneficiaries. Often named in a will or appointed by the court.",
      relatedLink: "/wrongful-death",
      relatedText: "Who can file a wrongful death claim"
    },
    {
      term: "Survival Action",
      category: "Wrongful Death",
      definition: "A claim for damages the deceased person suffered before death, such as pain and suffering or medical expenses. This is separate from a wrongful death claim for the beneficiaries' losses.",
      relatedLink: "/wrongful-death",
      relatedText: "Wrongful death vs. survival actions"
    }
  ];

  // Filter terms based on search
  const filteredTerms = glossaryTerms.filter(item =>
    item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.definition.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group terms by category
  const groupedTerms = filteredTerms.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  // Category icons
  const categoryIcons = {
    "Maritime Law": <Anchor className="w-5 h-5" />,
    "Personal Injury": <Scale className="w-5 h-5" />,
    "First Responder": <Shield className="w-5 h-5" />,
    "Premises Liability": <AlertTriangle className="w-5 h-5" />,
    "Wrongful Death": <BookOpen className="w-5 h-5" />
  };

  // FAQ data for schema
  const faqData = glossaryTerms.slice(0, 10).map(item => ({
    question: `What is ${item.term}?`,
    answer: item.definition
  }));

  return (
    <div className="min-h-screen pt-20" data-testid="legal-glossary-page">
      <SEOHead 
        title="Legal Glossary - Personal Injury & Maritime Law Terms | Amy C. Brown Law"
        description="Understand legal terminology used in personal injury, maritime law, and wrongful death cases. Definitions of Jones Act, maintenance and cure, statute of limitations, and more."
        faqData={faqData}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-12 md:pt-40 md:pb-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Legal Reference
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Legal Glossary
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Understanding legal terminology is the first step toward protecting your rights. This glossary explains common terms used in personal injury, maritime law, and wrongful death cases.
            </p>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="py-8 bg-slate-50 border-b border-slate-200 sticky top-20 z-40">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search legal terms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-lg"
              data-testid="glossary-search"
            />
          </div>
        </div>
      </section>

      {/* Glossary Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          {Object.keys(groupedTerms).length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 text-lg">No terms found matching "{searchTerm}"</p>
              <button 
                onClick={() => setSearchTerm('')}
                className="mt-4 text-teal-600 font-medium hover:text-teal-700"
              >
                Clear search
              </button>
            </div>
          ) : (
            Object.entries(groupedTerms).map(([category, terms]) => (
              <div key={category} className="mb-12" data-testid={`glossary-category-${category.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-3 mb-6 pb-3 border-b border-slate-200">
                  <span className="text-teal-600">{categoryIcons[category]}</span>
                  <h2 className="font-heading text-2xl font-bold text-secondary">
                    {category}
                  </h2>
                </div>
                
                <div className="space-y-6">
                  {terms.map((item, index) => (
                    <div 
                      key={index}
                      className="bg-slate-50 rounded-xl p-6 hover:bg-slate-100 transition-colors"
                      id={item.term.toLowerCase().replace(/\s+/g, '-')}
                    >
                      <h3 className="font-heading text-xl font-semibold text-secondary mb-3">
                        {item.term}
                      </h3>
                      <p className="text-slate-600 leading-relaxed mb-4">
                        {item.definition}
                      </p>
                      {item.relatedLink && (
                        <Link 
                          to={item.relatedLink}
                          className="inline-flex items-center gap-1 text-teal-600 font-medium hover:text-teal-700 text-sm"
                        >
                          {item.relatedText} <ChevronRight className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-2xl font-bold text-secondary text-center mb-8">
            Related Resources
          </h2>
          <div className="grid md:grid-cols-4 gap-4">
            <Link to="/faqs" className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow">
              <Scale className="w-8 h-8 text-teal-600 mx-auto mb-2" />
              <span className="text-secondary font-medium">FAQs</span>
            </Link>
            <Link to="/blog" className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow">
              <BookOpen className="w-8 h-8 text-teal-600 mx-auto mb-2" />
              <span className="text-secondary font-medium">Learning Center</span>
            </Link>
            <Link to="/compensation-estimator" className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow">
              <Car className="w-8 h-8 text-teal-600 mx-auto mb-2" />
              <span className="text-secondary font-medium">Case Calculator</span>
            </Link>
            <Link to="/pricing" className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow">
              <Shield className="w-8 h-8 text-teal-600 mx-auto mb-2" />
              <span className="text-secondary font-medium">Pricing Info</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Have Questions About Your Case?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Understanding legal terms is just the beginning. Contact me for a free consultation about your specific situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all shadow-lg"
              data-testid="glossary-cta-call"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LegalGlossaryPage;
